import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { api } from "../../../api/apiService";
import { Spinner } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";

const MonthlyReportPayroll = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const role = localStorage.getItem("role");

    const [payrollData, setPayrollData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [month, setMonth] = useState(null);
    const [year, setYear] = useState(null);
    const [title, setTitle] = useState("");

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const queryMonth = searchParams.get("month");
        const queryYear = searchParams.get("year");
        setMonth(queryMonth);
        setYear(queryYear);

        if (queryMonth && queryYear) {
            setTitle(`Payroll for ${getMonthName(queryMonth)} ${queryYear}`);
        }
    }, [location]);

    const getMonthName = (monthNumber) => {
        const months = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];
        return months[parseInt(monthNumber) - 1] || "";
    };

    const fetchPayrollData = async () => {
        if (!month || !year) return;
        setIsLoading(true);

        try {
            const response = await api.get(`/payrolls/make-payroll?month=${month}&year=${year}`);
            const dataWithDefaults = response.data.data.map(item => {
                const calc_days = item.total_attendances + (item.extra || 0);
                const total = calc_days * (item.pay_rate || 0);
                const grand_total = total + (item.bonus || 0);

                return {
                    ...item,
                    actual_days: item.total_attendances || 0,
                    extra: item.extra || 0,
                    calc_days,
                    pay_rate: item.pay_rate || 0,
                    total,
                    bonus: item.bonus || 0,
                    grand_total,
                };
            });
            setPayrollData(dataWithDefaults);
        } catch (error) {
            console.error("Error fetching payroll data:", error);
            toast.error("Failed to fetch payroll data");
        } finally {
            setIsLoading(false);
        }
    };


    useEffect(() => {
        fetchPayrollData();
    }, [month, year]);

    const handleInputChange = (index, field, value) => {
        const updatedData = payrollData.map((item, idx) => {
            if (idx === index) {
                const updatedItem = {
                    ...item,
                    [field]: value === "" ? null : parseFloat(value) || 0,
                };

                if (field === "calc_days") {
                    updatedItem.calc_days = updatedItem.calc_days;
                    updatedItem.actual_days = Math.max(0, updatedItem.calc_days - updatedItem.extra);
                } else {
                    updatedItem.calc_days = updatedItem.actual_days + updatedItem.extra;
                }

                updatedItem.total = updatedItem.calc_days * updatedItem.pay_rate;
                updatedItem.grand_total = updatedItem.total + updatedItem.bonus;
                updatedItem.grand_total = Math.round(updatedItem.grand_total * 10) / 10;
                return updatedItem;
            }
            return item;
        });

        setPayrollData(updatedData);
    };

    const handleSavePayroll = async () => {
        if (!title.trim()) {
            toast.error("Please enter a title for the payroll");
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                title,
                "payroll_entries": payrollData
            };

            const response = await api.post("/payrolls/save-payroll", payload);
            toast.success(response?.data?.message || "Payroll saved successfully");
            setTimeout(() => {
                navigate(`/user/payrolls/${response?.data?.data?.id}/details`)
            }, 1000);

        } catch (error) {
            if (error.data && error.data.errors) {
                error.data.errors.forEach(err => {
                    toast.error(err);
                });
            } else {
                toast.error("Failed to save payroll");
            }
        } finally {
            setIsSaving(false);
        }
    };

    const totals = payrollData.reduce(
        (acc, item) => {
            acc.actual_days += item.actual_days;
            acc.extra += item.extra;
            acc.calc_days += item.calc_days;
            acc.pay_rate += item.pay_rate;
            acc.bonus += item.bonus;
            acc.grand_total += item.grand_total;

            acc.actual_days = Math.round(acc.actual_days * 10) / 10;
            acc.extra = Math.round(acc.extra * 10) / 10;
            acc.calc_days = Math.round(acc.calc_days * 10) / 10;
            acc.pay_rate = Math.round(acc.pay_rate * 10) / 10;
            acc.bonus = Math.round(acc.bonus * 10) / 10;
            acc.grand_total = Math.round(acc.grand_total * 10) / 10;

            return acc;
        },
        { actual_days: 0, extra: 0, calc_days: 0, pay_rate: 0, bonus: 0, grand_total: 0 }
    );

    return (
        <>
            <main id="main" className="main">
                <div className="pagetitle d-flex justify-content-between align-items-center mb-4">
                    <h1>Monthly Overall Payroll</h1>
                </div>

                <section className="section">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="card">
                                <div className="card-body">
                                    <h5 className="card-title">Payroll Data - {getMonthName(month)} {year}</h5>
                                    <div className="row mb-3 g-2">
                                        <div className="col-md-4">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Enter payroll title"
                                                value={title}
                                                onChange={(e) => setTitle(e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <button
                                                className="btn btn-primary"
                                                onClick={handleSavePayroll}
                                                disabled={isLoading || isSaving}
                                            >
                                                {isSaving ? (
                                                    <>
                                                        <Spinner
                                                            as="span"
                                                            animation="border"
                                                            size="sm"
                                                            className="me-2"
                                                        />
                                                        Saving...
                                                    </>
                                                ) : (
                                                    "Save"
                                                )}
                                            </button>
                                        </div>
                                        <div className="col-4"></div>
                                    </div>
                                    {isLoading ? (
                                        <div className="text-center mt-3">
                                            <Spinner animation="border" variant="primary" />
                                            <span className="ms-2">Loading...</span>
                                        </div>
                                    ) : (
                                        <>
                                            {payrollData.length > 0 ? (
                                                <div className="table-responsive">
                                                    <table className="table">
                                                        <thead>
                                                            <tr>
                                                                <th>Name</th>
                                                                <th width="130">Actual Days</th>
                                                                <th width="130">Extra</th>
                                                                <th width="130">Total Days</th>
                                                                <th width="130">Pay Rate</th>
                                                                <th width="130">Bonus</th>
                                                                <th width="130" className="text-end">Grand Total</th>
                                                                {/* <th width="130" className="text-end">Payroll Date</th> */}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {payrollData.map((item, index) => (
                                                                <tr key={item.employee_id}>
                                                                    <td>{item.name}</td>
                                                                    <td>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control"
                                                                            readOnly
                                                                            value={item.actual_days}
                                                                            onChange={(e) =>
                                                                                handleInputChange(index, "actual_days", e.target.value)
                                                                            }
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control"
                                                                            value={item.extra}
                                                                            onChange={(e) =>
                                                                                handleInputChange(index, "extra", e.target.value)
                                                                            }
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control"
                                                                            value={item.calc_days}
                                                                            onChange={(e) =>
                                                                                handleInputChange(index, "calc_days", e.target.value)
                                                                            }
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control text-end"
                                                                            value={item.pay_rate}
                                                                            onChange={(e) =>
                                                                                handleInputChange(index, "pay_rate", e.target.value)
                                                                            }
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control text-end"
                                                                            value={item.bonus}
                                                                            onChange={(e) =>
                                                                                handleInputChange(index, "bonus", e.target.value)
                                                                            }
                                                                        />
                                                                    </td>
                                                                    <td className="text-end">{item.grand_total.toFixed(2)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                        <tfoot>
                                                            <tr>
                                                                <th className="text-end">Total:</th>
                                                                <td>{totals.actual_days}</td>
                                                                <td>{totals.extra}</td>
                                                                <td>{totals.calc_days}</td>
                                                                <td className="text-end">{totals.pay_rate > 0 ? totals.pay_rate.toFixed(2) : "0.00"}</td>
                                                                <td className="text-end">{totals?.bonus}</td>
                                                                <td className="text-end">{totals.grand_total.toFixed(2)}</td>
                                                                <td className="text-end"></td>
                                                            </tr>
                                                        </tfoot>
                                                    </table>
                                                </div>
                                            ) : (
                                                <p>No payroll data available.</p>
                                            )}
                                        </>)
                                    }

                                </div>
                            </div>
                        </div>
                    </div>
                </section>

            </main>
        </>
    );
};

export default MonthlyReportPayroll;
